import { AudioCue, AudioTrigger } from "../types/audio";

export class AudioCueManager {
  private cues: Map<AudioTrigger, AudioCue[]> = new Map();
  private audioContext: AudioContext | null = null;
  private masterVolume: number = 0.7;
  private isEnabled: boolean = true;

  constructor() {
    this.initializeDefaultCues();
  }

  /**
   * Initialize audio context (must be called after user interaction)
   */
  async initialize(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
  }

  /**
   * Initialize default audio cues
   */
  private initializeDefaultCues(): void {
    // Using data URLs for simple beep sounds as placeholders
    // In production, these would be actual audio files
    const defaultCues: AudioCue[] = [
      {
        id: "dice-roll-1",
        name: "Dice Roll",
        soundUrl:
          "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2i57uihUBELTqPh7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXx",
        trigger: AudioTrigger.DICE_ROLL,
        volume: 0.5,
        priority: 1,
      },
      {
        id: "critical-hit-1",
        name: "Critical Hit",
        soundUrl:
          "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2i57uihUBELTqPh7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXx",
        trigger: AudioTrigger.CRITICAL_HIT,
        volume: 0.8,
        priority: 2,
      },
      {
        id: "combat-start-1",
        name: "Combat Start",
        soundUrl:
          "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2i57uihUBELTqPh7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXx",
        trigger: AudioTrigger.COMBAT_START,
        volume: 0.6,
        priority: 2,
      },
      {
        id: "turn-change-1",
        name: "Turn Change",
        soundUrl:
          "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2i57uihUBELTqPh7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXxz3opBSh+zPLaizsKGGS46+mnVhMNTKXi7bllHAU2jdXx",
        trigger: AudioTrigger.TURN_CHANGE,
        volume: 0.4,
        priority: 1,
      },
    ];

    defaultCues.forEach((cue) => {
      const existingCues = this.cues.get(cue.trigger) || [];
      existingCues.push(cue);
      this.cues.set(cue.trigger, existingCues);
    });
  }

  /**
   * Trigger an audio cue
   */
  async triggerCue(trigger: AudioTrigger, context?: unknown): Promise<void> {
    if (!this.isEnabled) {
      return;
    }

    const cues = this.cues.get(trigger);
    if (!cues || cues.length === 0) {
      return;
    }

    // Select the first cue (or implement selection logic based on context)
    const selectedCue = this.selectCue(cues, context);

    if (selectedCue) {
      await this.playAudioCue(selectedCue);
    }
  }

  /**
   * Select which cue to play (can be extended with more sophisticated logic)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private selectCue(cues: AudioCue[], _context?: unknown): AudioCue | null {
    if (cues.length === 0) return null;

    // For now, just return the first cue
    // In the future, this could select based on context, random selection, etc.
    return cues[0];
  }

  /**
   * Play an audio cue
   */
  private async playAudioCue(cue: AudioCue): Promise<void> {
    try {
      // Ensure audio context is initialized
      if (!this.audioContext) {
        await this.initialize();
      }

      if (!this.audioContext) {
        console.error("Failed to initialize audio context");
        return;
      }

      // Create audio element
      const audio = new Audio(cue.soundUrl);
      audio.volume = cue.volume * this.masterVolume;

      // Play the sound
      await audio.play();
    } catch (error) {
      console.error("Failed to play audio cue:", error);
    }
  }

  /**
   * Add a custom audio cue
   */
  addCue(cue: AudioCue): void {
    const existingCues = this.cues.get(cue.trigger) || [];
    existingCues.push(cue);
    this.cues.set(cue.trigger, existingCues);
  }

  /**
   * Remove an audio cue
   */
  removeCue(cueId: string): void {
    this.cues.forEach((cues, trigger) => {
      const filtered = cues.filter((cue) => cue.id !== cueId);
      this.cues.set(trigger, filtered);
    });
  }

  /**
   * Set master volume
   */
  setMasterVolume(volume: number): void {
    this.masterVolume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Enable/disable audio cues
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Get all cues for a specific trigger
   */
  getCuesForTrigger(trigger: AudioTrigger): AudioCue[] {
    return this.cues.get(trigger) || [];
  }

  /**
   * Cleanup
   */
  async cleanup(): Promise<void> {
    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }
  }
}

export default AudioCueManager;
